import React, { FC, HTMLAttributes } from 'react'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes'
import clsx from 'clsx'

export interface MessageProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * If added, the message will show an icon before the message's label.
   */
  icon?: IconDefinition
  /**
   * The width of the message.
   */
  full?: boolean
  /**
   * The theme of the message.
   */
  theme?: 'danger' | 'warning' | 'success' | 'neutral'

  /**
   * Call when the message should be dismissed.
   */
  dismiss?: () => void
}

export const MessageStyle = {
  _: 'font-body flex items-center gap-6 p-6 text-base rounded-md',
  full: ['w-fit', 'w-full'],
  theme: {
    danger: 'bg-error-default text-info-inverse',
    neutral: 'bg-white text-info-inverse',
    success: 'bg-white text-info-inverse',
    warning: 'bg-warning-default text-info-inverse'
  }
}

/**
 * Message - Flash messages should be used anytime we need to communicate app status with the user. Things like submitting a form, deleting an item, or a loading error should be shown with these.
 */
export const Message: FC<MessageProps> = React.forwardRef<HTMLDivElement, MessageProps>(
  ({ children, className, full = false, icon, theme = 'neutral', ...props }, ref) => {
    return (
      <div
        ref={ref}
        {...props}
        className={clsx(MessageStyle._, MessageStyle.full[~~full], MessageStyle.theme[theme], className)}
      >
        {icon ? <FontAwesomeIcon icon={icon} size='xl' /> : null}
        {children}
        {props.dismiss ? (
          <button aria-label='Close' onClick={props.dismiss} title='Close'>
            <FontAwesomeIcon icon={faTimes} size='lg' />
          </button>
        ) : null}
      </div>
    )
  }
)
